import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import {
  QueryClient,
  QueryClientProvider,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClientConfig,
} from '@tanstack/react-query';
import { writeToken, readToken, type ApiError } from './http';
import {
  analyticsApi,
  approvalsApi,
  archiveApi,
  auditApi,
  authApi,
  certificatesApi,
  invoicesApi,
  inboxApi,
  notificationsApi,
  peppolApi,
  reportsApi,
  rulesApi,
  settingsApi,
  tddApi,
  templatesApi,
  tenantsApi,
  webhooksApi,
  complianceApi,
  onboardingApi,
  type InvoiceListFilter,
} from './api';
import type {
  CreateInvoiceInput,
  SignInRequest,
  UserDto,
} from './types';

// ---------------------------------------------------------------
// QueryClient + AuthContext
// ---------------------------------------------------------------

const defaultConfig: QueryClientConfig = {
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        const apiErr = error as ApiError;
        if (apiErr?.status >= 400 && apiErr?.status < 500) return false;
        return failureCount < 2;
      },
    },
  },
};

export function makeQueryClient(): QueryClient {
  return new QueryClient(defaultConfig);
}

interface AuthContextValue {
  user: UserDto | undefined;
  token: string | null;
  status: 'idle' | 'loading' | 'authenticated' | 'unauthenticated' | 'error';
  signIn: (input: SignInRequest) => Promise<UserDto>;
  signOut: () => void;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export interface SolaraApiProviderProps {
  children: ReactNode;
  queryClient?: QueryClient;
}

export function SolaraApiProvider({ children, queryClient }: SolaraApiProviderProps) {
  const [client] = useState(() => queryClient ?? makeQueryClient());
  return (
    <QueryClientProvider client={client}>
      <AuthProvider>{children}</AuthProvider>
    </QueryClientProvider>
  );
}

function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() => readToken());
  const [user, setUser] = useState<UserDto | undefined>(undefined);
  const [status, setStatus] = useState<AuthContextValue['status']>(
    () => (readToken() ? 'loading' : 'unauthenticated'),
  );

  const refresh = useMemo(
    () => async () => {
      if (!readToken()) {
        setUser(undefined);
        setStatus('unauthenticated');
        return;
      }
      setStatus('loading');
      try {
        const u = await authApi.me();
        setUser(u);
        setStatus('authenticated');
      } catch {
        writeToken(null);
        setToken(null);
        setUser(undefined);
        setStatus('unauthenticated');
      }
    },
    [],
  );

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const signIn = async (input: SignInRequest) => {
    setStatus('loading');
    try {
      const res = await authApi.signIn(input);
      writeToken(res.accessToken);
      setToken(res.accessToken);
      setUser(res.user);
      setStatus('authenticated');
      return res.user;
    } catch (e) {
      setStatus('error');
      throw e;
    }
  };

  const signOut = () => {
    writeToken(null);
    setToken(null);
    setUser(undefined);
    setStatus('unauthenticated');
  };

  const value = useMemo<AuthContextValue>(
    () => ({ user, token, status, signIn, signOut, refresh }),
    [user, token, status, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <SolaraApiProvider>');
  return ctx;
}

// ---------------------------------------------------------------
// Tenant hooks
// ---------------------------------------------------------------

export const useTenants = (params?: { status?: string; search?: string }) =>
  useQuery({
    queryKey: ['tenants', params],
    queryFn: () => tenantsApi.list(params),
  });

export const useTenant = (id: string | undefined) =>
  useQuery({
    queryKey: ['tenant', id],
    queryFn: () => tenantsApi.get(id!),
    enabled: !!id,
  });

export function useUpdateTenant(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: Parameters<typeof tenantsApi.update>[1]) => tenantsApi.update(id, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tenant', id] });
      qc.invalidateQueries({ queryKey: ['tenants'] });
    },
  });
}

export function useSuspendTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) => tenantsApi.suspend(id, reason),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tenants'] }),
  });
}

// ---------------------------------------------------------------
// Invoice hooks
// ---------------------------------------------------------------

export const useInvoices = (filter?: InvoiceListFilter) =>
  useQuery({
    queryKey: ['invoices', filter],
    queryFn: () => invoicesApi.list(filter),
  });

export const useAdminInvoices = (filter?: InvoiceListFilter) =>
  useQuery({
    queryKey: ['invoices', 'admin', filter],
    queryFn: () => invoicesApi.listForAdmin(filter),
  });

export const useInvoice = (id: string | undefined) =>
  useQuery({
    queryKey: ['invoice', id],
    queryFn: () => invoicesApi.get(id!),
    enabled: !!id,
    refetchInterval: (query) => {
      const inv = query.state.data;
      if (!inv) return false;
      // Poll while in flight: anything not in a terminal state.
      const terminal = ['ACKNOWLEDGED', 'ARCHIVED', 'CANCELLED', 'FAILED', 'REJECTED'];
      return terminal.includes(inv.status) ? false : 2_000;
    },
  });

export function useCreateInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateInvoiceInput) => invoicesApi.create(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['invoice-stats'] });
    },
  });
}

export function useAcknowledgeInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => invoicesApi.acknowledge(id),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['invoice', data.id] });
      qc.invalidateQueries({ queryKey: ['invoices'] });
    },
  });
}

export const useInvoiceStats = () =>
  useQuery({
    queryKey: ['invoice-stats'],
    queryFn: () => invoicesApi.countByStatus(),
  });

// ---------------------------------------------------------------
// Validation rules
// ---------------------------------------------------------------

export const useValidationRules = (params?: { tenantId?: string; status?: string }) =>
  useQuery({ queryKey: ['rules', params], queryFn: () => rulesApi.list(params) });

export const useValidationRule = (id: string | undefined) =>
  useQuery({ queryKey: ['rule', id], queryFn: () => rulesApi.get(id!), enabled: !!id });

export function useToggleRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => rulesApi.toggle(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rules'] }),
  });
}

// ---------------------------------------------------------------
// Peppol
// ---------------------------------------------------------------

export const usePeppolParticipants = (params?: { tenantId?: string }) =>
  useQuery({ queryKey: ['peppol-participants', params], queryFn: () => peppolApi.participants(params) });

export const usePeppolTransactions = (params?: {
  direction?: string;
  state?: string;
  tenantId?: string;
  limit?: number;
}) => useQuery({ queryKey: ['peppol-transactions', params], queryFn: () => peppolApi.transactions(params) });

export function usePeppolLookup() {
  return useMutation({ mutationFn: (pid: string) => peppolApi.participantLookup(pid) });
}

export function usePeppolRegister() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: peppolApi.registerParticipant,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['peppol-participants'] }),
  });
}

export function usePeppolActivate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (participantId: string) => peppolApi.activate(participantId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['peppol-participants'] }),
  });
}

export function usePeppolPublish() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (participantId: string) => peppolApi.publish(participantId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['peppol-participants'] }),
  });
}

// ---------------------------------------------------------------
// Webhooks (admin)
// ---------------------------------------------------------------

export const useWebhooks = (params?: { delivery?: string; limit?: number }) =>
  useQuery({ queryKey: ['webhooks', params], queryFn: () => webhooksApi.list(params) });

export function useReplayWebhook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => webhooksApi.replay(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['webhooks'] }),
  });
}

// ---------------------------------------------------------------
// TDD / archive / approvals / inbox / templates / notifications
// ---------------------------------------------------------------

export const useTddSubmissions = (params?: { tenantId?: string; status?: string }) =>
  useQuery({ queryKey: ['tdd', params], queryFn: () => tddApi.list(params) });

export function useSubmitTdd() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (invoiceId: string) => tddApi.submit(invoiceId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tdd'] }),
  });
}

export const useArchive = (params?: { limit?: number; tenantId?: string }) =>
  useQuery({ queryKey: ['archive', params], queryFn: () => archiveApi.list(params) });

export function useVerifyArchive() {
  return useMutation({ mutationFn: (id: string) => archiveApi.verify(id) });
}

export const useApprovals = (params?: { state?: string; tenantId?: string }) =>
  useQuery({ queryKey: ['approvals', params], queryFn: () => approvalsApi.list(params) });

export function useApproveApproval() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) => approvalsApi.approve(id, reason),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['approvals'] }),
  });
}

export function useRejectApproval() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => approvalsApi.reject(id, reason),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['approvals'] }),
  });
}

export const useInbox = (params?: { category?: string; search?: string }) =>
  useQuery({ queryKey: ['inbox', params], queryFn: () => inboxApi.list(params) });

export function useMarkInboxRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => inboxApi.markRead(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['inbox'] }),
  });
}

export const useTemplates = () =>
  useQuery({ queryKey: ['templates'], queryFn: () => templatesApi.list() });

export function useCreateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; payload: unknown }) => templatesApi.create(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['templates'] }),
  });
}

export function useDeleteTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => templatesApi.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['templates'] }),
  });
}

export const useNotifications = () =>
  useQuery({
    queryKey: ['notifications'],
    queryFn: () => notificationsApi.list(),
    refetchInterval: 15_000,
  });

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => notificationsApi.markRead(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });
}

// ---------------------------------------------------------------
// Audit + analytics + certificates + reports + settings
// ---------------------------------------------------------------

export const useAuditEvents = (params?: {
  tenantId?: string;
  resource?: string;
  since?: string;
  limit?: number;
}) => useQuery({ queryKey: ['audit', params], queryFn: () => auditApi.list(params) });

export const useSpendOverview = (params?: { tenantId?: string }) =>
  useQuery({ queryKey: ['analytics-spend', params], queryFn: () => analyticsApi.spend(params) });

export const useVatOverview = (params?: { tenantId?: string }) =>
  useQuery({ queryKey: ['analytics-vat', params], queryFn: () => analyticsApi.vat(params) });

export const useRecoveryFindings = (params?: { tenantId?: string; status?: string }) =>
  useQuery({ queryKey: ['analytics-recovery', params], queryFn: () => analyticsApi.recovery(params) });

export function useSetRecoveryStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      analyticsApi.setRecoveryStatus(id, status),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['analytics-recovery'] }),
  });
}

export const useCertificates = (params?: { type?: string }) =>
  useQuery({ queryKey: ['certificates', params], queryFn: () => certificatesApi.list(params) });

/** FTA-accreditation compliance posture (data residency, HSM, PINT-AE, EmaraTax). */
export const useCompliancePosture = () =>
  useQuery({
    queryKey: ['compliance', 'posture'],
    queryFn: () => complianceApi.posture(),
    staleTime: 10 * 60_000,
  });

export const useOnboardingState = () =>
  useQuery({ queryKey: ['onboarding', 'state'], queryFn: () => onboardingApi.getState() });

export function useAuthorizeEmaraTax() {
  return useMutation({
    mutationFn: (callbackBase?: string) => onboardingApi.authorizeEmaraTax(callbackBase),
  });
}

export function useCompleteEmaraTax() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ code, channel }: { code: string; channel: 'inbound' | 'outbound' }) =>
      onboardingApi.completeEmaraTax(code, channel),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['onboarding', 'state'] }),
  });
}

export function useRotateCertificate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => certificatesApi.rotate(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['certificates'] }),
  });
}

export const useReports = (params?: { category?: string; since?: string; until?: string }) =>
  useQuery({ queryKey: ['reports', params], queryFn: () => reportsApi.list(params) });

export function useGenerateReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { category: string; period: string }) => reportsApi.generate(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['reports'] }),
  });
}

export const useSettings = () =>
  useQuery({ queryKey: ['settings'], queryFn: () => settingsApi.get() });

export function useUpdateSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: Record<string, unknown>) => settingsApi.update(patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['settings'] }),
  });
}

export const useApiKeys = () =>
  useQuery({ queryKey: ['api-keys'], queryFn: () => settingsApi.apiKeys() });

export const useErpConnectors = () =>
  useQuery({ queryKey: ['erp-connectors'], queryFn: () => settingsApi.erpConnectors() });
